import CloseIcon from '@mui/icons-material/Close';
import {
  Grid,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  Box,
  IconButton,
} from '@mui/material';
import { assertNotNull } from '@remote-voice/utilities';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LineClampTypography } from '@/components/atoms/LineClampTypography';
import { RoomInvitation } from '@/components/organisms/invitingRoomDialog/InvitingRoomDialog';
import {
  ChatRoom,
  ChatSessionState,
  UserRole,
  useGuideUsersLazyQuery,
} from '@/types/graphql';

const RoomListItem = (props: {
  chatRoom: ChatRoom;
  onInvite: (value: RoomInvitation) => void;
  onDelete: () => Promise<void>;
  onClose: () => Promise<void>;
}) => {
  const { t } = useTranslation('admin');
  const [guideUsersLazy] = useGuideUsersLazyQuery();
  const navigate = useNavigate();

  const sm = useMediaQuery('(min-width:600px)');
  // const { t: commonT } = useTranslation('common');

  // const [roomMenuAnchor, setRoomMenuAnchor] = useState<null | HTMLElement>(
  //   null
  // );
  // const [showDownlaodCsvDialog, setShowDownlaodCsvDialog] = useState(false);

  return (
    <>
      <Box
        sx={{
          bgcolor: (t) => t.palette.background.default,
          borderRadius: 2,
          pl: 2,
          pr: 1,
          py: 3,
        }}
        data-testid="room-list-item"
      >
        <Grid container alignItems={sm ? 'center' : 'stretch'}>
          <Grid item xs={11} sm={6} order={1}>
            <Stack justifyContent="flex-start" spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <LineClampTypography
                  rowCount={1}
                  variant="h6"
                  sx={{
                    maxWidth: sm ? '100%' : '70%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {props.chatRoom.name}
                </LineClampTypography>

                {!sm && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1, px: 1 }}
                    onClick={() => navigate(`${props.chatRoom.id}/edit`)}
                  >
                    {t('room.roomSetting')}
                  </Button>
                )}
              </Stack>
              <Stack flexDirection="row" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ color: (t) => t.palette.grey[600], marginRight: 2 }}
                >
                  {(() => {
                    const date = new Date(props.chatRoom.createdAt);
                    return `${t(
                      'room.createdDate'
                    )}：${date.toLocaleDateString()}`;
                  })()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: (t) => t.palette.common.black }}
                >
                  {t('room.roomStatus')}
                </Typography>
                {props.chatRoom.isChatRoomEnable ? (
                  <Typography component="span" variant="body2" color="error">
                    {t('room.valid')}
                  </Typography>
                ) : (
                  <Typography component="span" variant="body2">
                    {t('room.invalid')}
                  </Typography>
                )}
                {props.chatRoom.isChatRoomEnable ? (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={props.onClose}
                  >
                    <CloseIcon />
                  </IconButton>
                ) : (
                  <></>
                )}
              </Stack>
            </Stack>
          </Grid>
          {/* <Grid item xs={1} order={sm ? 3 : 2}>
            <IconButton
              sx={{ p: sm ? 'auto' : 0 }}
              onClick={(e) => setRoomMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          </Grid> */}
          <Grid item xs={12} sm={5} order={sm ? 2 : 3}>
            <Stack
              justifyContent={'center'}
              flexDirection={'row'}
              alignItems={'center'}
              sx={{ height: '100%' }}
              mt={sm ? 0 : 1}
            >
              <Button
                variant="outlined"
                sx={{
                  bgcolor: (t) => t.palette.common.white,
                  minWidth: '80px',
                  width: '30%',
                  px: 0,
                }}
                onClick={async () => {
                  // 入室コードの取得
                  const currentSession = props.chatRoom.sessions.find(
                    (session) =>
                      session.state === ChatSessionState.NotStarted ||
                      session.state === ChatSessionState.Progressing
                  );
                  assertNotNull(currentSession);

                  // ルームに紐づくガイドユーザ情報の取得
                  const users = await guideUsersLazy({
                    variables: {
                      input: {
                        tenantCompanyId: props.chatRoom.tenantCompanyId,
                      },
                    },
                  });
                  if (users.error) throw users.error;
                  // 現状ガイドユーザーは各テナントにつき一つしか存在しないため、配列の最初の要素を取得している
                  const guide = users.data?.guideUsers.filter(
                    (x) => x.role === UserRole.Guide
                  )?.[0];
                  if (guide == null) throw new Error('Guide is not found');

                  const entryCode = currentSession.entryCode;
                  props.onInvite({
                    url: `${location.origin}/chat/${props.chatRoom.id}`,
                    entryCode: entryCode ?? undefined,
                    guideUrl: `${location.origin}/chat/${props.chatRoom.id}/?chatUserId=${guide.chatUserId}&chatUserPassword=${guide.chatUserPassword}`,
                    chatRoomId: props.chatRoom.id,
                    guideLoginId: props.chatRoom.guideLoginId,
                    guideLoginPassword: props.chatRoom.guideLoginPassword,
                  });
                }}
              >
                {t('room.invite')}
              </Button>

              <Button
                sx={{
                  ml: 1,
                  minWidth: '80px',
                  width: '30%',
                  px: 0,
                }}
                onClick={async () => {
                  // テナントアドミンで参加
                  const users = await guideUsersLazy({
                    variables: {
                      input: {
                        tenantCompanyId: props.chatRoom.tenantCompanyId,
                      },
                    },
                  });
                  if (users.error) throw users.error;
                  // 現状テナントアドミンユーザーは各テナントにつき一つしか存在しないため、配列の最初の要素を取得している
                  const tenantAdmin = users.data?.guideUsers.filter(
                    (x) => x.role === UserRole.TenantCompanyAdmin
                  )?.[0];
                  if (tenantAdmin == null)
                    throw new Error('Tenant admin user is not found');
                  navigate(
                    `/chat/${props.chatRoom.id}/?chatUserId=${tenantAdmin.chatUserId}&chatUserPassword=${tenantAdmin.chatUserPassword}`
                  );
                }}
              >
                {t('room.start')}
              </Button>
              {sm && (
                <Button
                  variant="outlined"
                  sx={{
                    bgcolor: (t) => t.palette.common.white,
                    ml: 1,
                    minWidth: '80px',
                    width: '30%',
                    px: 0,
                  }}
                  onClick={() => navigate(`${props.chatRoom.id}/edit`)}
                >
                  {t('room.roomSetting')}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
      {/* <Menu
        open={!!roomMenuAnchor}
        anchorEl={roomMenuAnchor}
        onClose={() => setRoomMenuAnchor(null)}
      > */}

      {/* TODO: 画面サイズによって出現の条件分岐 */}
      {/* <MenuItem
          onClick={() => {
            setShowDownlaodCsvDialog(true);
            setRoomMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          <ListItemText>{t('room.downloadLogs')}</ListItemText>
        </MenuItem> */}
      {/* {props.chatRoom.isChatRoomEnable && (
          <MenuItem
            onClick={() => {
              setRoomMenuAnchor(null);
              props.onClose();
            }}
          >
            <ListItemIcon>
              <CloseIcon />
            </ListItemIcon>
            <ListItemText>{t('room.closeRoom')}</ListItemText>
          </MenuItem>
        )}
      </Menu> */}
      {/* <DownloadCsvDialogByRoomId
        open={showDownlaodCsvDialog}
        onClose={() => setShowDownlaodCsvDialog(false)}
        roomId={props.chatRoom.id}
      /> */}
    </>
  );
};
export default RoomListItem;
